import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Divider
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { fetchDriverById, createDriver, updateDriver } from '../../redux/slices/driversSlice';
import { fetchVehicles } from '../../redux/slices/vehiclesSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import EntityDocumentsSection from '../common/EntityDocumentsSection';
import { uploadPendingEntityDocuments } from '../../services/entityDocumentsService';
import { phoneRecognitionHint } from '../../utils/phoneNumber';

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const DriverForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const isEditMode = !!id;
  
  const { currentDriver, isLoading: driverLoading, error: driverError } = useSelector(state => state.drivers);
  const { vehicles, isLoading: vehiclesLoading } = useSelector(state => state.vehicles);
  
  const [formData, setFormData] = useState({
    // Fahrer-Informationen
    driverNumber: '',
    license_number: '',
    license_expiry: null,
    documents: [],
    
    // Benutzer-Informationen (wird nur bei Erstellung verwendet)
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  
  const [formErrors, setFormErrors] = useState({
    driverNumber: '',
    license_number: '',
    license_expiry: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  
  // Neuanlage: Dateien werden vorgemerkt und nach dem Anlegen hochgeladen,
  // weil der Storage-Pfad die Fahrer-ID braucht.
  const [pendingDocumentFiles, setPendingDocumentFiles] = useState([]);
  const [savingDocuments, setSavingDocuments] = useState(false);
  const documentUploadError = location.state?.documentUploadError || '';
  // Nach dem Anlegen ohne Fahrernummer (Nummer war schon vergeben) landet der
  // Nutzer hier im Bearbeiten-Formular und kann direkt eine andere eintragen.
  const driverNumberWarning = location.state?.driverNumberWarning || '';
  // Fehler beim Speichern bleiben im Formular — die Eingaben gehen nicht verloren.
  const [submitError, setSubmitError] = useState('');
  const [, setAvailableVehicles] = useState([]);
  
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchDriverById(id));
    }
    
    dispatch(fetchVehicles());
  }, [dispatch, id, isEditMode]);
  
  useEffect(() => {
    if (isEditMode && currentDriver) {
      setFormData(prev => ({
        ...prev,
        name: currentDriver.user?.name || '',
        phone: currentDriver.user?.phone || '',
        driverNumber: currentDriver.driverNumber || '',
        license_number: currentDriver.license_number || '',
        license_expiry: currentDriver.license_expiry ? new Date(currentDriver.license_expiry) : null,
        documents: currentDriver.documents || [],
      }));
    }
  }, [currentDriver, isEditMode]);
  
  useEffect(() => {
    // Filter verfügbare Fahrzeuge (die keinem Fahrer zugewiesen sind oder dem aktuellen Fahrer)
    if (vehicles && vehicles.length > 0) {
      const availableVehicles = vehicles.filter(vehicle => 
        !vehicle.current_driver_id || (isEditMode && vehicle.current_driver_id === id)
      );
      setAvailableVehicles(availableVehicles);
    }
  }, [vehicles, id, isEditMode]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Fehler zurücksetzen, wenn der Benutzer ein Feld ändert
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      license_expiry: date
    });
    
    if (formErrors.license_expiry) {
      setFormErrors({
        ...formErrors,
        license_expiry: ''
      });
    }
  };
  
  // Dokumente speichert EntityDocumentsSection direkt am Fahrer; hier nur
  // die Anzeige nachziehen.
  const handleDocumentsChange = (documents) => {
    setFormData(prev => ({ ...prev, documents }));
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    console.log('Validating form data:', formData);
    
    // Führerschein-Ablaufdatum validieren - machen wir optional
    // if (!formData.license_expiry) {
    //   newErrors.license_expiry = 'Führerschein-Ablaufdatum ist erforderlich';
    //   valid = false;
    // }
    
    // Name und Telefon in beiden Modi validieren
    if (!formData.name) {
      newErrors.name = 'Name ist erforderlich';
      valid = false;
    }
    if (!formData.phone) {
      newErrors.phone = 'Telefonnummer ist erforderlich';
      valid = false;
    }

    // Bei Neuanlage müssen weitere Felder validiert werden
    if (!isEditMode) {
      // E-Mail validieren
      if (!formData.email) {
        newErrors.email = 'E-Mail ist erforderlich';
        valid = false;
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(formData.email)) {
        newErrors.email = 'Ungültige E-Mail-Adresse';
        valid = false;
      }

      // Passwort validieren (nur bei Neuanlage)
      if (!formData.password) {
        newErrors.password = 'Passwort ist erforderlich';
        console.log('Validation failed: Password missing');
        valid = false;
      } else if (formData.password.length < 6) {
        newErrors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
        console.log('Validation failed: Password too short');
        valid = false;
      }
      
      // Passwort-Bestätigung validieren
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwort-Bestätigung ist erforderlich';
        console.log('Validation failed: Password confirmation missing');
        valid = false;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
        console.log('Validation failed: Passwords do not match');
        valid = false;
      }
    }
    
    console.log('Validation errors:', newErrors);
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('handleSubmit called');
    setSubmitError('');
    
    if (validateForm()) {
      console.log('Form validated successfully');
      
      const driverData = {
        driverNumber: formData.driverNumber.trim(),
        license_number: formData.license_number,
        license_expiry: formData.license_expiry?.toISOString(),
        ...(isEditMode && {
          user: {
            name: formData.name,
            phone: formData.phone,
          }
        })
      };

      if (isEditMode) {
        dispatch(updateDriver({
          id,
          ...driverData
        })).then((result) => {
          console.log('Update result:', result);
          if (result.error) {
            showSubmitError(result.payload);
            return;
          }
          navigate(`/fahrer/${id}`);
        }).catch(error => {
          console.error('Error updating driver:', error);
        });
      } else {
        console.log('Creating new driver with data:', {
          ...driverData,
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        });
        
        // Create new driver with auto-generated ID
        dispatch(createDriver({
          ...driverData,
          // Diese Felder werden für die Erstellung eines neuen Benutzerkontos verwendet
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password
        })).then(async (result) => {
          console.log('Create result:', result);
          if (result.error) {
            console.error('Error in result:', result.error);
            showSubmitError(result.payload);
            return;
          }
          const newId = result.payload?.id;
          const driverNumberConflict = result.payload?.driverNumberConflict;
          let failed = [];
          if (newId && pendingDocumentFiles.length > 0) {
            setSavingDocuments(true);
            failed = await uploadPendingEntityDocuments('driver', newId, pendingDocumentFiles);
            setSavingDocuments(false);
          }
          if (newId && (failed.length > 0 || driverNumberConflict)) {
            navigate(`/fahrer/${newId}/bearbeiten`, {
              state: {
                ...(failed.length > 0 && {
                  documentUploadError: `Der Fahrer wurde angelegt, aber diese Dokumente konnten nicht hochgeladen werden: ${failed.join(', ')}. Bitte erneut hochladen.`
                }),
                ...(driverNumberConflict && {
                  driverNumberWarning: `Der Fahrer wurde angelegt, aber ohne Fahrernummer: ${driverNumberConflict} Bitte eine andere Nummer eintragen und speichern.`
                })
              }
            });
            return;
          }
          navigate('/fahrer');
        }).catch(error => {
          console.error('Error creating driver:', error);
        });
      }
    } else {
      console.log('Form validation failed');
    }
  };
  
  const showSubmitError = (message) => {
    const text = message || 'Speichern fehlgeschlagen';
    setSubmitError(text);
    if (text.startsWith('Fahrernummer')) {
      setFormErrors(prev => ({ ...prev, driverNumber: text }));
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/fahrer/${id}`);
    } else {
      navigate('/fahrer');
    }
  };
  
  const isLoading = driverLoading || vehiclesLoading;
  
  if (isLoading && isEditMode) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (driverError && isEditMode && currentDriver?.id !== id) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={driverError}
        onRetry={() => isEditMode && dispatch(fetchDriverById(id))}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Fahrer bearbeiten' : 'Neuen Fahrer anlegen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrer', path: '/fahrer' },
          { label: isEditMode ? 'Bearbeiten' : 'Neu', path: isEditMode ? `/fahrer/${id}/bearbeiten` : '/fahrer/neu' }
        ]}
      />
      
      <form onSubmit={handleSubmit}>
        {/* Hauptinformationen */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Fahrer-Informationen
          </Typography>
          {driverNumberWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {driverNumberWarning}
            </Alert>
          )}
          <Grid container spacing={3}>
            {isEditMode && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    name="name"
                    label="Vollständiger Name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Telefonnummer"
                    value={formData.phone}
                    onChange={handleChange}
                    error={!!formErrors.phone || phoneRecognitionHint(formData.phone).error}
                    helperText={formErrors.phone || phoneRecognitionHint(formData.phone).text}
                  />
                </Grid>
                <Grid size={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
              </>
            )}
            {!isEditMode && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    name="name"
                    label="Vollständiger Name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    name="email"
                    label="E-Mail"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Telefonnummer"
                    value={formData.phone}
                    onChange={handleChange}
                    error={!!formErrors.phone || phoneRecognitionHint(formData.phone).error}
                    helperText={formErrors.phone || phoneRecognitionHint(formData.phone).text}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="password"
                    name="password"
                    label="Passwort"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Passwort bestätigen"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={!!formErrors.confirmPassword}
                    helperText={formErrors.confirmPassword}
                  />
                </Grid>
                <Grid size={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
              </>
            )}
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="driverNumber"
                name="driverNumber"
                label="Fahrernummer"
                value={formData.driverNumber}
                onChange={handleChange}
                error={!!formErrors.driverNumber}
                helperText={formErrors.driverNumber || "Optional, z.B. 17 oder 017"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="license_number"
                name="license_number"
                label="Führerscheinnummer"
                value={formData.license_number}
                onChange={handleChange}
                error={!!formErrors.license_number}
                helperText={formErrors.license_number}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                <DatePicker
                  label="P-Schein gültig bis"
                  value={formData.license_expiry}
                  onChange={handleDateChange}
                  slots={{
                    textField: TextField
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!formErrors.license_expiry,
                      helperText: formErrors.license_expiry
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </FormPaper>
        
        {/* Dokumente */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Dokumente
          </Typography>
          
          {documentUploadError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {documentUploadError}
            </Alert>
          )}
          <EntityDocumentsSection
            kind="driver"
            entityId={isEditMode ? id : null}
            documents={formData.documents}
            onDocumentsChange={handleDocumentsChange}
            pendingFiles={pendingDocumentFiles}
            onPendingFilesChange={setPendingDocumentFiles}
          />
        </FormPaper>
        
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading || savingDocuments}
          >
            {savingDocuments ? 'Dokumente werden hochgeladen …' : isEditMode ? 'Änderungen speichern' : 'Fahrer erstellen'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default DriverForm;
